import { useEffect, useState } from "react";
import type {
  CookOven as CookOvenData,
  RecipeMatch,
} from "../api/cook";
import { ItemSprite } from "./ItemSprite";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";

interface Props {
  oven: CookOvenData;
  name: string;
  hasCoal: boolean;
  match: RecipeMatch;
  itemNames: Map<string, string>;
  onIgnite: () => void;
  onSlotClick: (slotIdx: number) => void;
  onCook: () => void;
  onCollect: () => void;
}

const FUEL_MAX = 100;
const COOK_FUEL_COST = 10;

// Tick a local clock 4×/s while cooking, so the progress bar
// animates smoothly between server-driven `oven_changed` diffs.
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

// Matches Cooking.html / cooking.jsx in the design folder. The oven
// is a dark metal shell with a layered black/tan/black inset border
// and a chunky 6px ink drop-shadow; the hearth inside it is a black
// cavity that gets a radial glow + flicker animation when lit.
//
// IMPORTANT: the hearth is a `<div>`, not a `<button>` — the slot
// and output cells inside it ARE buttons and HTML doesn't allow
// nesting them. Earlier versions of this file made the hearth a
// `<button>` and the inner slot clicks were silently dropped by the
// browser.
export function CookOven({
  oven,
  name,
  hasCoal,
  match,
  itemNames,
  onIgnite,
  onSlotClick,
  onCook,
  onCollect,
}: Props) {
  const theme = pixelTheme;
  const lit = oven.fuel > 0;
  const cooking = oven.cooking != null;
  const filledCount = oven.slots.filter((s) => s !== null).length;
  // The "Click to Light" affordance is only valid when the oven is
  // genuinely empty AND cold. A cook in flight, an uncollected output,
  // or ingredients sitting in slots all keep us in the contents view —
  // even if the cook just drained fuel to 0. Mirrors the server-side
  // `check_unlit_and_empty` guard in `Doi.Cook.Session`.
  const needsLighting =
    !lit && !cooking && oven.output == null && filledCount === 0;

  const canCook =
    oven.unlocked &&
    lit &&
    !cooking &&
    oven.output == null &&
    oven.fuel >= COOK_FUEL_COST &&
    match.kind === "match";

  const cookTitle = !oven.unlocked
    ? `Requires Cooking Level ${oven.unlock_level}`
    : !lit
      ? "Light the oven first."
      : oven.output != null
        ? "Collect output first."
        : oven.fuel < COOK_FUEL_COST
          ? "Not enough fuel."
          : match.kind === "match"
            ? `Cook ${itemNames.get(match.recipe.output) ?? match.recipe.output}`
            : match.kind === "locked"
              ? `Requires Cooking Level ${match.recipe.level}`
              : filledCount === 0
                ? "Add ingredients."
                : "No recipe matches these ingredients.";

  const now = useNow(cooking);
  const cookProgress = (() => {
    if (!oven.cooking) return 0;
    const total = oven.cooking.seconds * 1000;
    const elapsed = now - oven.cooking.started_at;
    return Math.max(0, Math.min(1, elapsed / total));
  })();

  // ---- locked: same layout skeleton as an unlocked oven (header +
  // hearth-sized body + button-row spacer) so heights stay aligned
  // across the grid. Just dim the whole thing to 70% opacity so the
  // kitchen brick shows through.
  if (!oven.unlocked) {
    return (
      <div
        data-testid={`cook-oven-${oven.idx}`}
        style={{ ...ovenShellStyle(), opacity: 0.7 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: "0.1em",
              color: E32.tan,
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              letterSpacing: "0.12em",
              padding: "3px 6px",
              border: `2px solid ${E32.coral}`,
              color: E32.coral,
            }}
          >
            LOCKED
          </div>
        </div>

        {/* Plaque sized to match the unlocked oven's hearth (border +
            min-height + padding are identical so the rows line up). */}
        <div
          style={{
            position: "relative",
            width: "100%",
            padding: 14,
            minHeight: 200,
            display: "grid",
            placeItems: "center",
            border: `3px solid ${E32.ink}`,
            background: "#1a1421",
            color: E32.coral,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          REQUIRES COOKING LV {oven.unlock_level}
        </div>

        {/* Invisible spacer matching the unlocked oven's cook-button
            row (10px top margin + ~36px button height + 2px border). */}
        <div style={{ marginTop: 10, height: 38 }} aria-hidden="true" />
      </div>
    );
  }

  // ---- unlocked
  const fuelPct = Math.max(0, Math.min(100, (oven.fuel / FUEL_MAX) * 100));
  const hearthCursor = !needsLighting
    ? "default"
    : hasCoal
      ? "pointer"
      : "not-allowed";

  return (
    <div data-testid={`cook-oven-${oven.idx}`} style={ovenShellStyle()}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: "0.1em",
            color: E32.tan,
          }}
        >
          {name}
        </div>
        <div
          className={cooking ? "cook-state-blink" : undefined}
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.12em",
            padding: "3px 6px",
            border: `2px solid ${cooking ? E32.amber : lit ? E32.butter : E32.tan}`,
            color: cooking ? E32.amber : lit ? E32.butter : E32.tan,
          }}
        >
          {cooking ? "COOKING" : lit ? "LIT" : "COLD"}
        </div>
      </div>

      {/* HEARTH (DIV, not button — see top-of-file comment) */}
      <div
        role={needsLighting ? "button" : undefined}
        tabIndex={needsLighting ? 0 : undefined}
        aria-label={
          needsLighting
            ? hasCoal
              ? `${name}: light with coal`
              : `${name}: no coal in inventory`
            : undefined
        }
        onClick={needsLighting && hasCoal ? onIgnite : undefined}
        onKeyDown={
          needsLighting && hasCoal
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onIgnite();
                }
              }
            : undefined
        }
        className={lit ? "cook-hearth-lit" : "cook-hearth-unlit"}
        aria-disabled={!needsLighting || !hasCoal}
        style={{
          position: "relative",
          width: "100%",
          padding: 14,
          minHeight: 200,
          display: "grid",
          gap: 10,
          border: `3px solid ${E32.ink}`,
          background: lit
            ? "radial-gradient(ellipse at 50% 110%, rgba(254,231,97,0.35), transparent 60%), #0a0710"
            : "#1a1421",
          boxShadow: lit
            ? "inset 0 -60px 80px -10px rgba(254,174,52,0.45), inset 0 0 60px -10px rgba(228,59,68,0.35)"
            : "none",
          cursor: hearthCursor,
          overflow: "hidden",
          color: E32.parchment,
          font: "inherit",
          textAlign: "left",
        }}
      >
        {needsLighting && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              pointerEvents: "none",
            }}
          >
            <ItemSprite id="coal" size={56} />
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 13,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: E32.parchment,
                textShadow: `1px 1px 0 ${E32.ink}`,
              }}
            >
              {hasCoal ? "Click to Light" : "Unlit"}
            </div>
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 11,
                letterSpacing: "0.08em",
                color: hasCoal ? E32.butter : E32.coral,
                textShadow: `1px 1px 0 ${E32.ink}`,
              }}
            >
              {hasCoal ? "— 1 COAL · +100 FUEL —" : "NO COAL IN INVENTORY"}
            </div>
          </div>
        )}

        {!needsLighting && (
          <>
            <div
              style={{
                position: "relative",
                zIndex: 2,
                display: "flex",
                // Five items in the row: slot, +, slot, arrow, output.
                // `space-between` distributes them evenly with the
                // outer two pinned to the edges.
                justifyContent: "space-between",
                alignItems: "center",
                gap: 4,
              }}
            >
              {oven.slots.map((slot, i) => (
                <SlotCell
                  key={i}
                  slot={slot}
                  slotIndex={i}
                  ovenName={name}
                  itemName={slot ? itemNames.get(slot) ?? slot : null}
                  disabled={cooking}
                  onClick={() => onSlotClick(i)}
                  connectorAfter={i === 0}
                />
              ))}
              <ArrowConnector />
              <OutputCell
                output={oven.output}
                outputName={
                  oven.output
                    ? itemNames.get(oven.output) ?? oven.output
                    : null
                }
                ovenName={name}
                onClick={onCollect}
              />
            </div>

            <div
              style={{
                position: "relative",
                zIndex: 2,
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center",
                gap: 8,
                color: E32.tan,
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              <span style={{ width: 38 }}>FUEL</span>
              <div
                style={{
                  position: "relative",
                  height: 10,
                  background: E32.ink,
                  border: `2px solid ${E32.tan}`,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    right: "auto",
                    width: `${fuelPct}%`,
                    background: E32.amber,
                    boxShadow: "inset 0 2px 0 rgba(255,255,255,0.25)",
                    transition: "width 200ms linear",
                  }}
                />
              </div>
              <span
                style={{
                  color: E32.parchment,
                  minWidth: 56,
                  textAlign: "right",
                  textShadow: `1px 1px 0 ${E32.ink}`,
                }}
              >
                {Math.round(oven.fuel)} / 100
              </span>
            </div>

            {/* Always render the progress row so clicking Cook doesn't
                introduce a layout shift. Hidden via `visibility` while
                idle so the row still occupies its slot in the flex
                column. */}
            <div
              style={{
                position: "relative",
                zIndex: 2,
                height: 6,
                background: E32.ink,
                border: `2px solid ${E32.tan}`,
                overflow: "hidden",
                visibility: cooking ? "visible" : "hidden",
              }}
              aria-hidden={!cooking || undefined}
            >
              <div
                data-testid={`cook-progress-${oven.idx}`}
                style={{
                  height: "100%",
                  width: `${cookProgress * 100}%`,
                  background: E32.butter,
                  transition: "width 200ms linear",
                }}
              />
            </div>
          </>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <button
          data-testid={`cook-button-${oven.idx}`}
          type="button"
          disabled={!canCook}
          onClick={onCook}
          title={cookTitle}
          aria-hidden={needsLighting || undefined}
          tabIndex={needsLighting ? -1 : undefined}
          style={{
            width: "100%",
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            padding: "10px 12px",
            background: canCook ? E32.amber : E32.plum,
            color: canCook ? E32.ink : E32.tan,
            border: `2px solid ${E32.ink}`,
            cursor: canCook ? "pointer" : "not-allowed",
            opacity: canCook ? 1 : 0.85,
            visibility: needsLighting ? "hidden" : "visible",
          }}
        >
          {cooking
            ? "COOKING…"
            : oven.output != null
              ? "COLLECT FIRST"
              : match.kind === "match"
                ? `COOK ${(itemNames.get(match.recipe.output) ?? match.recipe.output).toUpperCase()}`
                : match.kind === "locked"
                  ? `REQUIRES COOKING LV ${match.recipe.level}`
                  : filledCount === 0
                    ? "ADD INGREDIENTS"
                    : "NO RECIPE"}
        </button>
      </div>
    </div>
  );
}

// Match the design's layered oven shell exactly: 4px plum outer
// border, three inset rings (ink, tan, ink) totalling another 6px of
// banding, and a chunky 6×6 ink drop-shadow offset.
function ovenShellStyle(): React.CSSProperties {
  return {
    background: E32.ink,
    border: `4px solid ${E32.plum}`,
    boxShadow: `
      inset 0 0 0 2px ${E32.ink},
      inset 0 0 0 4px ${E32.tan},
      inset 0 0 0 6px ${E32.ink},
      6px 6px 0 0 ${E32.ink}
    `,
    padding: "16px 14px 14px",
    color: E32.parchment,
    position: "relative",
  };
}

function SlotCell({
  slot,
  slotIndex,
  ovenName,
  itemName,
  disabled,
  onClick,
  connectorAfter,
}: {
  slot: string | null;
  slotIndex: number;
  ovenName: string;
  itemName: string | null;
  disabled: boolean;
  onClick: () => void;
  connectorAfter: boolean;
}) {
  const aria = slot
    ? `${ovenName} slot ${slotIndex + 1}: ${itemName ?? slot}. Click to change.`
    : `${ovenName} slot ${slotIndex + 1}: empty. Click to add ingredient.`;
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        aria-label={aria}
        className={!slot && !disabled ? "cook-slot-empty" : undefined}
        style={{
          width: 64,
          height: 64,
          display: "grid",
          placeItems: "center",
          background: "rgba(24,20,37,0.6)",
          border: `2px solid ${E32.tan}`,
          cursor: disabled ? "default" : "pointer",
          color: E32.tan,
          opacity: disabled ? 0.55 : 1,
          padding: 0,
        }}
      >
        {slot ? (
          <ItemSprite id={slot} size={48} />
        ) : (
          <span
            style={{
              opacity: 0.5,
              fontFamily: pixelTheme.fontDisplay,
              fontSize: 18,
              color: E32.tan,
            }}
          >
            +
          </span>
        )}
      </button>
      {connectorAfter && <PlusConnector />}
    </>
  );
}

// Pixel-art connectors come from authored 8×8 PNGs in
// /public/cooking/. They ship as white-on-transparent so we can tint
// them at runtime: the PNG is used as a CSS `mask-image` and the
// element's background-color paints through the mask's alpha. That
// keeps the pixel art crisp at any scale (with `imageRendering:
// pixelated`) and avoids the hairy filter chain that arbitrary
// white→color tinting otherwise requires.
const CONNECTOR_PX = 32; // 8×8 native, scaled 4× for parity with the rest of the row

function PixelConnector({ src }: { src: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        flexShrink: 0,
        width: CONNECTOR_PX,
        height: CONNECTOR_PX,
        backgroundColor: E32.amber,
        WebkitMaskImage: `url('${src}')`,
        maskImage: `url('${src}')`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        imageRendering: "pixelated",
      }}
    />
  );
}

function PlusConnector() {
  return <PixelConnector src="/cooking/plus.png" />;
}

function ArrowConnector() {
  return <PixelConnector src="/cooking/right_arrow.png" />;
}

function OutputCell({
  output,
  outputName,
  ovenName,
  onClick,
}: {
  output: string | null;
  outputName: string | null;
  ovenName: string;
  onClick: () => void;
}) {
  const empty = output == null;
  const aria = empty
    ? `${ovenName} output: empty`
    : `${ovenName} output: ${outputName ?? output}. Press Enter to collect.`;
  return (
    <button
      type="button"
      disabled={empty}
      onClick={onClick}
      aria-label={aria}
      title={empty ? "Output" : `Collect ${outputName ?? output}`}
      data-testid="cook-output"
      // Per design: subtle amber wash always (rgba(254,174,52,0.12))
      // — never a solid yellow fill — with an amber border. When an
      // item lands, add a thin butter inset glow + outer butter ring.
      style={{
        width: 64,
        height: 64,
        display: "grid",
        placeItems: "center",
        background: "rgba(254, 174, 52, 0.12)",
        border: `2px solid ${E32.amber}`,
        boxShadow: empty
          ? "none"
          : `inset 0 0 0 1px ${E32.butter}, 0 0 0 2px rgba(254,231,97,0.4)`,
        cursor: empty ? "default" : "pointer",
        color: empty ? E32.tan : E32.parchment,
        fontFamily: pixelTheme.fontDisplay,
        fontSize: 18,
        padding: 0,
      }}
    >
      {output ? (
        <ItemSprite id={output} size={48} />
      ) : (
        <span style={{ opacity: 0.5, color: E32.tan }}>?</span>
      )}
    </button>
  );
}
